// Mock Eventbrite service that simulates API behavior
// Used as a reliable fallback when the real API is unavailable

import { NormalizedEvent } from './eventbriteService';
import { format, addDays } from 'date-fns';

export interface MockEventParams {
  q?: string;
  city?: string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  onlineEvents?: boolean;
  page?: number;
  pageSize?: number;
}

// Generate realistic event data for testing and development
export const generateMockEvents = (params: MockEventParams): {
  events: NormalizedEvent[];
  pagination: { object_count: number; page_count: number; page_size: number; page_number: number };
} => {
  // Create initial pool of events
  const eventPool: NormalizedEvent[] = [
    {
      id: 'evt-001',
      title: 'Chicago Tech Conference 2025',
      description: 'Join us for the largest tech conference in Chicago featuring workshops, keynotes, and networking opportunities.',
      startDate: format(addDays(new Date(), 15), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 17), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/chicago-tech-conference',
      location: {
        name: 'Chicago Convention Center',
        address: '123 Main St',
        city: 'Chicago',
        state: 'IL',
        postalCode: '60601',
        country: 'US',
        latitude: '41.8781',
        longitude: '-87.6298'
      },
      isOnline: false,
      isFree: false,
      organizerName: 'Chicago Tech Association',
      imageUrl: 'https://picsum.photos/id/0/800/400',
      categories: ['Technology', 'Business'],
      tags: ['tech', 'conference', 'networking'],
      source: 'eventbrite',
      sourceId: 'eb-001',
      rawData: {}
    },
    {
      id: 'evt-002',
      title: 'Online Web Development Workshop',
      description: 'Learn modern web development techniques from industry experts in this interactive online workshop.',
      startDate: format(addDays(new Date(), 5), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 5), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/web-dev-workshop',
      location: {},
      venue: 'Online Event',
      isOnline: true,
      isFree: true,
      organizerName: 'Code Academy',
      imageUrl: 'https://picsum.photos/id/1/800/400',
      categories: ['Technology', 'Education'],
      tags: ['webdev', 'javascript', 'workshop'],
      source: 'eventbrite',
      sourceId: 'eb-002',
      rawData: {}
    },
    {
      id: 'evt-003',
      title: 'Chicago Food Festival',
      description: 'Experience the best culinary delights Chicago has to offer at this annual food festival.',
      startDate: format(addDays(new Date(), 10), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 11), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/chicago-food-fest',
      location: {
        name: 'Millennium Park',
        address: '201 E Randolph St',
        city: 'Chicago',
        state: 'IL',
        postalCode: '60602',
        country: 'US',
        latitude: '41.8826',
        longitude: '-87.6233'
      },
      isOnline: false,
      isFree: false,
      organizerName: 'Chicago Food Network',
      imageUrl: 'https://picsum.photos/id/292/800/400',
      categories: ['Food & Drink'],
      tags: ['food', 'festival', 'culinary'],
      source: 'eventbrite',
      sourceId: 'eb-003',
      rawData: {}
    },
    {
      id: 'evt-004',
      title: 'Virtual Photography Workshop',
      description: 'Improve your photography skills with professional guidance in this engaging virtual workshop.',
      startDate: format(addDays(new Date(), 7), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 7), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/photography-workshop',
      location: {},
      venue: 'Online Event',
      isOnline: true,
      isFree: false,
      organizerName: 'Creative Photography',
      imageUrl: 'https://picsum.photos/id/250/800/400',
      categories: ['Arts'],
      tags: ['photography', 'workshop', 'creative'],
      source: 'eventbrite',
      sourceId: 'eb-004',
      rawData: {}
    },
    {
      id: 'evt-005',
      title: 'San Francisco Marathon Training',
      description: 'Get ready for the San Francisco Marathon with this group training session.',
      startDate: format(addDays(new Date(), 12), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 12), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/sf-marathon-training',
      location: {
        name: 'Golden Gate Park',
        address: '501 Stanyan St',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94117',
        country: 'US',
        latitude: '37.7694',
        longitude: '-122.4862'
      },
      isOnline: false,
      isFree: true,
      organizerName: 'SF Running Club',
      imageUrl: 'https://picsum.photos/id/175/800/400',
      categories: ['Sports & Fitness'],
      tags: ['running', 'marathon', 'training'],
      source: 'eventbrite',
      sourceId: 'eb-005',
      rawData: {}
    },
    {
      id: 'evt-006',
      title: 'New York Finance Summit',
      description: 'Connect with finance professionals and industry leaders at this premier networking event.',
      startDate: format(addDays(new Date(), 20), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 21), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/nyc-finance-summit',
      location: {
        name: 'Javits Center',
        address: '429 11th Ave',
        city: 'New York',
        state: 'NY',
        postalCode: '10001',
        country: 'US',
        latitude: '40.7578',
        longitude: '-73.9959'
      },
      isOnline: false,
      isFree: false,
      organizerName: 'Financial Industry Association',
      imageUrl: 'https://picsum.photos/id/325/800/400',
      categories: ['Business'],
      tags: ['finance', 'summit', 'networking'],
      source: 'eventbrite',
      sourceId: 'eb-006',
      rawData: {}
    },
    {
      id: 'evt-007',
      title: 'Online Yoga Retreat',
      description: 'Rejuvenate your mind and body with this immersive online yoga retreat led by expert instructors.',
      startDate: format(addDays(new Date(), 3), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 5), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/yoga-retreat',
      location: {},
      venue: 'Online Event',
      isOnline: true,
      isFree: false,
      organizerName: 'Mindful Yoga',
      imageUrl: 'https://picsum.photos/id/177/800/400',
      categories: ['Health & Wellness'],
      tags: ['yoga', 'wellness', 'retreat'],
      source: 'eventbrite',
      sourceId: 'eb-007',
      rawData: {}
    },
    {
      id: 'evt-008',
      title: 'Chicago Jazz Festival',
      description: 'Experience the soul of jazz at Chicago\'s premier annual music festival.',
      startDate: format(addDays(new Date(), 25), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 28), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/chicago-jazz',
      location: {
        name: 'Grant Park',
        address: '337 E Randolph St',
        city: 'Chicago',
        state: 'IL',
        postalCode: '60601',
        country: 'US',
        latitude: '41.8863',
        longitude: '-87.6205'
      },
      isOnline: false,
      isFree: true,
      organizerName: 'Chicago Department of Cultural Affairs',
      imageUrl: 'https://picsum.photos/id/96/800/400',
      categories: ['Music'],
      tags: ['jazz', 'music', 'festival'],
      source: 'eventbrite',
      sourceId: 'eb-008',
      rawData: {}
    },
    {
      id: 'evt-009',
      title: 'Digital Marketing Conference',
      description: 'Discover the latest trends and strategies in digital marketing from industry-leading experts.',
      startDate: format(addDays(new Date(), 14), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 15), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/digital-marketing-conf',
      location: {
        name: 'Hyatt Regency',
        address: '151 E Wacker Dr',
        city: 'Chicago',
        state: 'IL',
        postalCode: '60601',
        country: 'US',
        latitude: '41.8868',
        longitude: '-87.6236'
      },
      isOnline: false,
      isFree: false,
      organizerName: 'Marketing Professionals Association',
      imageUrl: 'https://picsum.photos/id/180/800/400',
      categories: ['Business', 'Technology'],
      tags: ['marketing', 'digital', 'conference'],
      source: 'eventbrite',
      sourceId: 'eb-009',
      rawData: {}
    },
    {
      id: 'evt-010',
      title: 'Virtual Book Club: Modern Fiction',
      description: 'Join our monthly book club focusing on contemporary fiction from diverse authors.',
      startDate: format(addDays(new Date(), 8), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 8), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/book-club',
      location: {},
      venue: 'Online Event',
      isOnline: true,
      isFree: true,
      organizerName: 'Literary Circle',
      imageUrl: 'https://picsum.photos/id/169/800/400',
      categories: ['Books'],
      tags: ['book-club', 'literature', 'reading'],
      source: 'eventbrite',
      sourceId: 'eb-010',
      rawData: {}
    },
    {
      id: 'evt-011',
      title: 'Chicago Craft Beer Festival',
      description: 'Sample the finest craft beers from local and regional breweries at this popular annual event.',
      startDate: format(addDays(new Date(), 18), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 19), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/craft-beer-fest',
      location: {
        name: 'Lincoln Park',
        address: '500-5700 N Lake Shore Dr',
        city: 'Chicago',
        state: 'IL',
        postalCode: '60614',
        country: 'US',
        latitude: '41.9299',
        longitude: '-87.6370'
      },
      isOnline: false,
      isFree: false,
      organizerName: 'Chicago Brewers Guild',
      imageUrl: 'https://picsum.photos/id/434/800/400',
      categories: ['Food & Drink'],
      tags: ['beer', 'craft', 'festival'],
      source: 'eventbrite',
      sourceId: 'eb-011',
      rawData: {}
    },
    {
      id: 'evt-012',
      title: 'Art Gallery Opening: Modern Perspectives',
      description: 'Be among the first to experience this innovative exhibition featuring emerging Chicago artists.',
      startDate: format(addDays(new Date(), 9), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      endDate: format(addDays(new Date(), 9), 'yyyy-MM-dd\'T\'HH:mm:ss'),
      url: 'https://example.com/events/art-gallery-opening',
      location: {
        name: 'River North Gallery',
        address: '415 N Wells St',
        city: 'Chicago',
        state: 'IL',
        postalCode: '60654',
        country: 'US',
        latitude: '41.8896',
        longitude: '-87.6339'
      },
      isOnline: false,
      isFree: true,
      organizerName: 'Chicago Arts Council',
      imageUrl: 'https://picsum.photos/id/1074/800/400',
      categories: ['Arts', 'Visual Arts'],
      tags: ['art', 'gallery', 'exhibition'],
      source: 'eventbrite',
      sourceId: 'eb-012',
      rawData: {}
    }
  ];

  // Filter events based on search parameters
  let filteredEvents = [...eventPool];

  // Apply text search filter
  if (params.q) {
    const searchTerm = params.q.toLowerCase();
    filteredEvents = filteredEvents.filter(event => 
      event.title.toLowerCase().includes(searchTerm) ||
      event.description.toLowerCase().includes(searchTerm) ||
      (event.tags?.some(tag => tag.toLowerCase().includes(searchTerm)) || false)
    );
  }

  // Apply city filter
  if (params.city) {
    const city = params.city.toLowerCase();
    filteredEvents = filteredEvents.filter(event => 
      !event.isOnline && 
      event.location?.city?.toLowerCase().includes(city)
    );
  }

  // Apply online events filter
  if (params.onlineEvents !== undefined) {
    filteredEvents = filteredEvents.filter(event => 
      event.isOnline === params.onlineEvents
    );
  }

  // Apply date range filters
  if (params.startDate) {
    const startDate = new Date(params.startDate).getTime();
    filteredEvents = filteredEvents.filter(event => 
      new Date(event.startDate).getTime() >= startDate
    );
  }

  if (params.endDate) {
    const endDate = new Date(params.endDate).getTime();
    filteredEvents = filteredEvents.filter(event => 
      new Date(event.startDate).getTime() <= endDate
    );
  }

  // Apply pagination
  const page = params.page || 1;
  const pageSize = params.pageSize || 10;
  const totalCount = filteredEvents.length;
  const pageCount = Math.ceil(totalCount / pageSize);
  
  const paginatedEvents = filteredEvents.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Return formatted response that matches the real API structure
  return {
    events: paginatedEvents,
    pagination: {
      object_count: totalCount,
      page_count: pageCount,
      page_size: pageSize,
      page_number: page
    }
  };
};

// Service interface that looks like the real Eventbrite service
export const mockEventbriteService = {
  searchEvents: async (params: MockEventParams) => {
    // Add a random delay to simulate network latency (200-800ms)
    const delay = Math.floor(Math.random() * 600) + 200;
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Generate mock data
    return generateMockEvents(params);
  },
  
  getEventById: async (eventId: string): Promise<NormalizedEvent | null> => {
    // Add a random delay to simulate network latency
    const delay = Math.floor(Math.random() * 400) + 100;
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Generate all events and find the requested one
    const allMockEvents = generateMockEvents({}).events;
    return allMockEvents.find(event => event.id === eventId) || null;
  }
};
